import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  OpsPaymentOrderStatus,
  OpsPaymentOrderSummary,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useListPaymentOrders,
  useReconcilePaymentOrder,
} from '../api/payments.ts';
import {
  formatLocalDateTimeInputValue,
  formatLocalTimestamp,
  localDateTimeInputValueToIso,
} from '../components/formatters.ts';
import { CustomerCell, CustomerCompanyLine } from '../components/identity/CustomerCell.tsx';
import { Modal } from '../components/Modal.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { auditDeepLink } from '../lib/auditDeepLink.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  FormField,
  Input,
  PageHeader,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  cx,
} from '../ui/index.ts';

/**
 * `/payments/orders` — operator view over `payment_orders`.
 *
 * Three surfaces:
 *   1. Filters bar — status pills (multi-select), account_id, date range.
 *      Default window is the past 7 days so the page opens on a bounded
 *      view rather than a full-table scan.
 *   2. Table — created / account (linked) / status chip / CNY / USD /
 *      applied rate / pay type / paid at / order id. Row click expands
 *      the detail panel with the raw notify payload + upstream trade
 *      number.
 *   3. Reconcile modal — on pending orders only. POSTs to
 *      `/v1/ops/payments/orders/:id/reconcile`; server handles the
 *      authoritative ZPay re-check + atomic credit batch. The modal
 *      shows the current order snapshot so the operator double-checks
 *      the amount before confirming.
 *
 * Amount display rule (per CLAUDE.md): internal state stays in integer
 * cents; render-time division is localised to the formatter helpers
 * below. The `/ 100` exists for display only — server never consumes
 * it back.
 */

const ALL_STATUSES: readonly OpsPaymentOrderStatus[] = [
  'pending',
  'paid',
  'expired',
  'failed',
  'refunded',
];

const STATUS_LABEL_KEY: Record<OpsPaymentOrderStatus, I18nKey> = {
  pending: 'payments_orders.status.pending',
  paid: 'payments_orders.status.paid',
  expired: 'payments_orders.status.expired',
  failed: 'payments_orders.status.failed',
  refunded: 'payments_orders.status.refunded',
};

type StatusChipVariant = 'ok' | 'warning' | 'error' | 'neutral';

const STATUS_VARIANT: Record<OpsPaymentOrderStatus, StatusChipVariant> = {
  pending: 'neutral',
  paid: 'ok',
  expired: 'warning',
  failed: 'error',
  refunded: 'warning',
};

const DEFAULT_WINDOW_DAYS = 7;
const PAGE_LIMIT = 50;

const FILTER_PILL_BASE =
  'inline-flex select-none items-center whitespace-nowrap rounded-full border px-3 py-1 text-micro font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 max-md:min-h-9 max-md:px-3';
const FILTER_PILL_ACTIVE = 'border-signal-deep/50 bg-signal-dim text-signal';
const FILTER_PILL_IDLE =
  'border-line bg-transparent text-paper-mid hover:border-line-strong hover:text-paper';

function formatUsdCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatCnyCents(cents: number): string {
  return `¥${(cents / 100).toFixed(2)}`;
}

function formatRate(rate: number): string {
  return rate.toFixed(4);
}

function formatTimestamp(iso: string | null): string {
  return formatLocalTimestamp(iso, { seconds: true });
}

function truncateId(value: string, head = 10, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function isoDateDaysAgo(days: number): string {
  const ms = Date.now() - days * 24 * 60 * 60 * 1000;
  return new Date(ms).toISOString();
}

export function PaymentsOrdersPage(): JSX.Element {
  const t = useT();
  // ── Filter state ──────────────────────────────────────────────────────
  // Status starts as empty (== "All") so the page opens wide; clicking
  // a pill toggles it in/out of the set. The `from` default of 7 days
  // ago caps the initial query window.
  const [selectedStatuses, setSelectedStatuses] = useState<
    readonly OpsPaymentOrderStatus[]
  >([]);
  const [accountIdInput, setAccountIdInput] = useState('');
  const [fromInput, setFromInput] = useState<string>(
    formatLocalDateTimeInputValue(isoDateDaysAgo(DEFAULT_WINDOW_DAYS)),
  );
  const [toInput, setToInput] = useState<string>('');
  // Expanded row state — only one row expanded at a time.
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  // Reconcile modal state — null when closed.
  const [reconcileTarget, setReconcileTarget] =
    useState<OpsPaymentOrderSummary | null>(null);

  const query = useMemo(
    () => ({
      status: selectedStatuses,
      accountId: accountIdInput.trim().length > 0 ? accountIdInput.trim() : undefined,
      from: fromInput.trim().length > 0 ? localDateTimeInputValueToIso(fromInput.trim()) : undefined,
      to: toInput.trim().length > 0 ? localDateTimeInputValueToIso(toInput.trim()) : undefined,
      limit: PAGE_LIMIT,
    }),
    [selectedStatuses, accountIdInput, fromInput, toInput],
  );

  const list = useListPaymentOrders(query);

  // Flatten infinite query pages into a single list.
  const orders = useMemo<readonly OpsPaymentOrderSummary[]>(
    () => list.data?.pages.flatMap((p) => p.orders) ?? [],
    [list.data],
  );

  function toggleStatus(s: OpsPaymentOrderStatus): void {
    setSelectedStatuses((prev) =>
      prev.includes(s) ? prev.filter((v) => v !== s) : [...prev, s],
    );
  }

  function handleAll(): void {
    setSelectedStatuses([]);
  }

  return (
    <section
      className="flex flex-col gap-6"
      data-testid="payments-orders-page"
    >
      <PageHeader
        title={t('payments_orders.title')}
        sub={t('payments_orders.subtitle')}
      />

      {/* ── Filters bar ─────────────────────────────────────────────── */}
      <div
        className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4"
        data-testid="payments-orders-filters"
      >
        <div
          className="flex flex-wrap items-center gap-2"
          role="group"
          aria-label={t('payments_orders.filter.status_aria_label')}
        >
          <span className="text-label text-paper-mid">
            {t('payments_orders.filter.status_label')}
          </span>
          <button
            type="button"
            onClick={handleAll}
            data-testid="payments-filter-status-all"
            aria-pressed={selectedStatuses.length === 0}
            className={cx(
              FILTER_PILL_BASE,
              selectedStatuses.length === 0 ? FILTER_PILL_ACTIVE : FILTER_PILL_IDLE,
            )}
          >
            {t('payments_orders.status.all')}
          </button>
          {ALL_STATUSES.map((s) => {
            const active = selectedStatuses.includes(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                data-testid={`payments-filter-status-${s}`}
                aria-pressed={active}
                className={cx(FILTER_PILL_BASE, active ? FILTER_PILL_ACTIVE : FILTER_PILL_IDLE)}
              >
                {t(STATUS_LABEL_KEY[s])}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField
            label={t('payments_orders.filter.account_id')}
            fieldId="payments-filter-account"
          >
            <Input
              id="payments-filter-account"
              type="text"
              mono
              placeholder={t('payments_orders.filter.account_id.placeholder')}
              value={accountIdInput}
              onChange={(e) => setAccountIdInput(e.currentTarget.value)}
              data-testid="payments-filter-account"
            />
          </FormField>
          <FormField label={t('payments_orders.filter.from')} fieldId="payments-filter-from">
            <Input
              id="payments-filter-from"
              type="datetime-local"
              mono
              value={fromInput}
              onChange={(e) => setFromInput(e.currentTarget.value)}
              data-testid="payments-filter-from"
            />
          </FormField>
          <FormField label={t('payments_orders.filter.to')} fieldId="payments-filter-to">
            <Input
              id="payments-filter-to"
              type="text"
              mono
              value={toInput}
              placeholder={t('payments_orders.filter.to.placeholder')}
              onChange={(e) => setToInput(e.currentTarget.value)}
              data-testid="payments-filter-to"
            />
          </FormField>
        </div>
      </div>

      {/* ── Results table ────────────────────────────────────────────── */}
      {list.isPending ? (
        <p className="m-0 text-body text-paper-low" data-testid="payments-orders-loading">
          {t('payments_orders.loading_aria')}
        </p>
      ) : null}
      {list.isError ? (
        <p
          className="m-0 rounded-lg border border-bad-line bg-bad-dim/40 p-4 text-body text-bad-bright"
          role="alert"
          data-testid="payments-orders-error"
        >
          {list.error instanceof OpsApiError
            ? `${list.error.code}: ${list.error.message}`
            : t('payments_orders.error.title')}
        </p>
      ) : null}
      {list.data !== undefined && orders.length === 0 ? (
        <p className="m-0 text-body text-paper-low" data-testid="payments-orders-empty">
          {t('payments_orders.empty')}
        </p>
      ) : null}

      {orders.length > 0 ? (
        <div className="flex flex-col gap-3" data-testid="payments-orders-table">
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>{t('payments_orders.column.created')}</Th>
                <Th>{t('payments_orders.column.account')}</Th>
                <Th>{t('payments_orders.column.status')}</Th>
                <Th num>{t('payments_orders.column.cny')}</Th>
                <Th num>{t('payments_orders.column.usd')}</Th>
                <Th num>{t('payments_orders.column.rate')}</Th>
                <Th>{t('payments_orders.column.pay')}</Th>
                <Th>{t('payments_orders.column.paid_at')}</Th>
                <Th>{t('payments_orders.column.order_id')}</Th>
                <Th className="text-right">{t('payments_orders.column.actions')}</Th>
              </Tr>
            </THead>
            <TBody>
              {orders.map((row) => {
                const expanded = expandedOrderId === row.order_id;
                return (
                  <OrderRow
                    key={row.order_id}
                    row={row}
                    expanded={expanded}
                    onToggleExpand={() =>
                      setExpandedOrderId((prev) =>
                        prev === row.order_id ? null : row.order_id,
                      )
                    }
                    onReconcile={() => setReconcileTarget(row)}
                  />
                );
              })}
            </TBody>
          </Table>
          <div className="flex items-center justify-between">
            <span
              className="text-label text-paper-low"
              data-testid="payments-orders-count"
            >
              {t('payments_orders.showing', { n: String(orders.length) })}
            </span>
            {list.hasNextPage ? (
              <Button
                variant="secondary"
                onClick={() => void list.fetchNextPage()}
                loading={list.isFetchingNextPage}
                data-testid="payments-orders-load-more"
              >
                {list.isFetchingNextPage
                  ? t('payments_orders.loading_more')
                  : t('payments_orders.load_more')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* ── Reconcile modal ──────────────────────────────────────────── */}
      {reconcileTarget !== null ? (
        <ReconcileModal
          target={reconcileTarget}
          onClose={() => setReconcileTarget(null)}
        />
      ) : null}
    </section>
  );
}

interface OrderRowProps {
  readonly row: OpsPaymentOrderSummary;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly onReconcile: () => void;
}

function OrderRow({
  row,
  expanded,
  onToggleExpand,
  onReconcile,
}: OrderRowProps): JSX.Element {
  const t = useT();
  const variant = STATUS_VARIANT[row.status];
  const accountLabel = row.account_email ?? row.account_id;
  const reconcilable = row.status === 'pending';
  return (
    <>
      <Tr data-testid={`payments-order-row-${row.order_id}`}>
        <Td mono>{formatTimestamp(row.created_at)}</Td>
        <Td>
          <CustomerCell
            accountId={row.account_id}
            testId={`payments-order-customer-${row.order_id}`}
            secondary={
              <Link
                to={`/customers/accounts/${encodeURIComponent(row.account_id)}`}
                className="self-start text-signal hover:underline"
                data-testid={`payments-order-account-${row.order_id}`}
              >
                {accountLabel}
              </Link>
            }
          />
        </Td>
        <Td>
          <StatusChip
            variant={variant}
            label={t(STATUS_LABEL_KEY[row.status])}
            testId={`payments-order-status-${row.order_id}`}
          />
        </Td>
        <Td num>
          <span className="text-ok">{formatCnyCents(row.amount_cny_cents)}</span>
        </Td>
        <Td num>{formatUsdCents(row.amount_usd_cents)}</Td>
        <Td num>{formatRate(row.applied_rate)}</Td>
        <Td mono>{row.pay_type}</Td>
        <Td mono>{formatTimestamp(row.paid_at)}</Td>
        <Td mono>{truncateId(row.order_id)}</Td>
        <Td className="text-right">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleExpand}
              data-testid={`payments-order-expand-${row.order_id}`}
              aria-expanded={expanded}
            >
              {expanded
                ? t('payments_orders.row.collapse')
                : t('payments_orders.row.details')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={onReconcile}
              disabled={!reconcilable}
              data-testid={`payments-order-reconcile-${row.order_id}`}
              aria-disabled={!reconcilable}
              title={
                reconcilable
                  ? t('payments_orders.row.reconcile_tooltip.available')
                  : t('payments_orders.row.reconcile_tooltip.disabled')
              }
            >
              {t('payments_orders.row.reconcile')}
            </Button>
          </div>
        </Td>
      </Tr>
      {expanded ? (
        <Tr interactive={false} data-testid={`payments-order-detail-${row.order_id}`}>
          <Td colSpan={10}>
            <div className="flex flex-col gap-3 rounded-lg bg-well p-4">
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.order_id')}
                value={row.order_id}
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.out_trade_no')}
                value={row.out_trade_no}
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.upstream_trade_no')}
                value={
                  row.upstream_trade_no ??
                  t('payments_orders.reconcile_modal.detail.empty')
                }
                mono
              />
              <DetailRow
                label={t('payments_orders.reconcile_modal.detail.expired_at')}
                value={formatTimestamp(row.expired_at)}
                mono
              />
            </div>
          </Td>
        </Tr>
      ) : null}
    </>
  );
}

interface DetailRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function DetailRow({ label, value, mono }: DetailRowProps): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-label text-paper-low">{label}</span>
      <span
        className={`break-all text-dense text-paper ${mono === true ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

interface ReconcileModalProps {
  readonly target: OpsPaymentOrderSummary;
  readonly onClose: () => void;
}

function ReconcileModal({ target, onClose }: ReconcileModalProps): JSX.Element {
  const t = useT();
  const reconcile = useReconcilePaymentOrder();
  const toast = useToast();
  const [note, setNote] = useState('');
  // Typed-confirmation gate — operator must type the order id exactly
  // before the Reconcile button activates. Matches the friction pattern
  // on the balance-adjust modal (AccountDetail). Prevents fat-finger
  // credits on wrong-row clicks.
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const canSubmit = confirmText === target.order_id && !reconcile.isPending;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    try {
      const result = await reconcile.mutateAsync({
        orderId: target.order_id,
        body: note.trim().length > 0 ? { note: note.trim() } : {},
      });
      if (result.reconciled) {
        toast.success(
          t('payments_orders.reconcile_modal.toast.credited', {
            orderId: target.order_id,
            status: result.newStatus,
          }),
          {
            action: {
              label: t('toast.action.view_audit'),
              // Audit row written by platform-api
              // `src/routes/internal-ops-payments.ts` (action
              // 'payment_order.reconcile', target = the order id).
              to: auditDeepLink('payment_order.reconcile', target.order_id),
            },
          },
        );
      } else if ('upstreamStatus' in result) {
        toast.info(
          t('payments_orders.reconcile_modal.toast.upstream', {
            orderId: target.order_id,
            status: result.upstreamStatus,
          }),
        );
      } else {
        toast.info(
          t('payments_orders.reconcile_modal.toast.already', {
            orderId: target.order_id,
            status: result.currentStatus,
          }),
        );
      }
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!reconcile.isPending) onClose();
      }}
      titleId="payments-reconcile-title"
      testId="payments-reconcile-modal"
    >
      <form className="flex flex-col gap-5 p-6" onSubmit={handleSubmit} noValidate>
        <h2 id="payments-reconcile-title" className="m-0 text-title text-paper">
          {t('payments_orders.reconcile_modal.title')}
        </h2>
        <p className="m-0 text-body text-paper-mid">
          {t('payments_orders.reconcile_modal.body', {
            orderId: target.order_id,
            amount: formatCnyCents(target.amount_cny_cents),
          })}
        </p>
        <dl className="m-0 grid grid-cols-2 gap-3 rounded-lg bg-well p-4 text-dense">
          <dt className="text-paper-low">
            {t('payments_orders.reconcile_modal.account')}
          </dt>
          <dd className="m-0 break-all font-mono text-paper">
            <div className="flex min-w-0 flex-col gap-0.5">
              <CustomerCompanyLine
                accountId={target.account_id}
                className="font-sans"
                testId="payments-reconcile-company"
              />
              <span>{target.account_email ?? target.account_id}</span>
            </div>
          </dd>
          <dt className="text-paper-low">
            {t('payments_orders.reconcile_modal.cny')}
          </dt>
          <dd className="m-0 font-mono text-ok">{formatCnyCents(target.amount_cny_cents)}</dd>
          <dt className="text-paper-low">
            {t('payments_orders.reconcile_modal.usd')}
          </dt>
          <dd className="m-0 font-mono text-paper-mid">{formatUsdCents(target.amount_usd_cents)}</dd>
          <dt className="text-paper-low">
            {t('payments_orders.reconcile_modal.applied_rate')}
          </dt>
          <dd className="m-0 font-mono text-paper">{formatRate(target.applied_rate)}</dd>
        </dl>
        <FormField
          label={t('payments_orders.reconcile_modal.note.label')}
          fieldId="payments-reconcile-note"
        >
          <Input
            id="payments-reconcile-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            placeholder={t('payments_orders.reconcile_modal.note.placeholder')}
            data-testid="payments-reconcile-note"
            disabled={reconcile.isPending}
            autoComplete="off"
          />
        </FormField>
        <FormField
          label={t('payments_orders.reconcile_modal.type_to_confirm')}
          fieldId="payments-reconcile-typed-confirm"
          hint={
            <>
              {t('payments_orders.reconcile_modal.type_hint_prefix')}
              <code className="font-mono text-paper">{target.order_id}</code>
              {t('payments_orders.reconcile_modal.type_hint_suffix')}
            </>
          }
        >
          <Input
            id="payments-reconcile-typed-confirm"
            type="text"
            mono
            value={confirmText}
            onChange={(e) => setConfirmText(e.currentTarget.value)}
            placeholder={target.order_id}
            data-testid="payments-reconcile-typed-confirm"
            disabled={reconcile.isPending}
            autoComplete="off"
            spellCheck={false}
          />
        </FormField>
        {error !== null ? (
          <p
            className="m-0 text-label text-bad"
            role="alert"
            data-testid="payments-reconcile-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              if (!reconcile.isPending) onClose();
            }}
            disabled={reconcile.isPending}
            data-testid="payments-reconcile-cancel"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
            loading={reconcile.isPending}
            data-testid="payments-reconcile-confirm"
          >
            {reconcile.isPending
              ? t('payments_orders.reconcile_modal.submitting')
              : t('payments_orders.reconcile_modal.submit')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
